import React, {FC} from 'react';

import {SelectionLocation} from '../../../../libs/shared/core/src/helpers/browser/domSelectionHelpers';
import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AnnotationAnnotatedResourcesEnum} from '../../../../libs/shared/core/src/models/api/annotationTypes';
import {getHasLinkedDiscussions} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {RepositionPopover} from '../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {CreateLinkedDiscussionDropdownMenuItem} from '../createLinkedDiscussionDropdownMenuItem';
import {FloatingToolbar} from '../floatingBars/floatingToolbar';
import {FloatingToolbarButton} from '../floatingBars/floatingToolbarButton';

export interface AnnotationHighlightViewerToolbarProps {
  currentLocation: SelectionLocation;
  isAnnotatingComment: boolean;
  canLinkConversation: boolean;
  onCreateAnnotatedComment: (type: AnnotationAnnotatedResourcesEnum) => void;
  onCreateAnnotatedLinkedConversation: () => void;
  onCreateAnnotatedLinkedDiscussion: () => void;
  onKeyboardCopy: () => void;
  onRequestClose: () => void;
}

export const AnnotationHighlightViewerToolbar: FC<AnnotationHighlightViewerToolbarProps> = (props) => {
  const {
    isAnnotatingComment,
    currentLocation: {anchor},
    onRequestClose,
  } = props;
  const hasLinkedDiscussions = useAppSelector(getHasLinkedDiscussions);

  const keyboardShortcutHandlers = {
    [KeyboardActionsEnum.COPY]: props.onKeyboardCopy,
    [KeyboardActionsEnum.CLOSE]: onRequestClose,
  };

  const renderForMessage = () => {
    if (hasLinkedDiscussions) {
      return (
        <Dropdown
          anchor={anchor}
          placement="bottom-end"
          onRequestClose={onRequestClose}
          isExclusive={false}
          isInline
        >
          <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
          <DropdownList>
            <DropdownItem
              onSelect={() => props.onCreateAnnotatedComment(AnnotationAnnotatedResourcesEnum.MESSAGE)}
            >
              <DropdownItemIcon
                label={<Trans id="annotations-viewer-comment">Comment</Trans>}
                iconName="discussionSmall"
                iconSize={16}
              />
              <DropdownItemContent>
                <Trans id="annotations-viewer-comment">Comment</Trans>
              </DropdownItemContent>
            </DropdownItem>
            <CreateLinkedDiscussionDropdownMenuItem
              onCreateLinkedConversation={props.onCreateAnnotatedLinkedConversation}
              onCreateLinkedDiscussion={props.onCreateAnnotatedLinkedDiscussion}
            />
          </DropdownList>
        </Dropdown>
      );
    }

    return (
      <RepositionPopover
        anchor={anchor}
        placement="bottom-end"
        onRequestClose={onRequestClose}
        isExclusive={false}
        isInline
      >
        <FloatingToolbar size={VisualSizesEnum.MEDIUM}>
          <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
          <FloatingToolbarButton
            iconName="discussionFilledSmall"
            label={<Trans id="annotations-viewer-comment">Comment</Trans>}
            size={VisualSizesEnum.LARGE}
            onClick={() => props.onCreateAnnotatedComment(AnnotationAnnotatedResourcesEnum.MESSAGE)}
            shouldRenderLabelInline
          />
        </FloatingToolbar>
      </RepositionPopover>
    );
  };

  const renderForComment = () => (
    <RepositionPopover
      anchor={anchor}
      placement="bottom-end"
      onRequestClose={onRequestClose}
      isExclusive={false}
      isInline
    >
      <FloatingToolbar size={VisualSizesEnum.MEDIUM}>
        <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
        <FloatingToolbarButton
          iconName="replySmall"
          label={<Trans id="annotations-viewer-reply">Reply</Trans>}
          size={VisualSizesEnum.LARGE}
          onClick={() => props.onCreateAnnotatedComment(AnnotationAnnotatedResourcesEnum.COMMENT)}
          shouldRenderLabelInline
        />
      </FloatingToolbar>
    </RepositionPopover>
  );

  return isAnnotatingComment ? renderForComment() : renderForMessage();
};
